<template>
  <div class="shorthand-urls mb-5">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div>
        <h3 class="mb-1">Shorthand URLs</h3>
        <p class="text-muted mb-0">
          These shortened URLs omit the client GUID. When a user visits one, the system automatically
          resolves the correct client and redirects to the full URL.
        </p>
      </div>
      <a href="/admin" class="btn btn-outline-secondary btn-sm">← Back to Admin</a>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-3">How it works</h5>
        <div class="row">
          <div class="col-md-6">
            <p class="mb-2"><strong>Client Resolution Order:</strong></p>
            <ol class="mb-0">
              <li>Extract the GUID from the referring page URL</li>
              <li>If the user has only one client, use that client</li>
              <li>Look up the last client the user viewed (from activity log)</li>
            </ol>
          </div>
          <div class="col-md-6">
            <p class="mb-2"><strong>User Type Handling:</strong></p>
            <ul class="mb-0">
              <li><strong>Not logged in</strong> — redirected to login, then back to the shorthand URL</li>
              <li><strong>No client</strong> — redirected to generic benchmarks</li>
              <li><strong>Single client</strong> — automatically uses that client</li>
              <li><strong>Multiple clients</strong> — uses the last-viewed client</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <input type="text" v-model="search" class="form-control" placeholder="Search URLs..." />
    </div>

    <div v-for="section in groupedSections" :key="section.name" class="mb-4">
      <h5 class="mb-2">{{ section.name }}</h5>
      <div class="table-responsive">
        <table class="table table-bordered table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 25%">Bare URL</th>
              <th style="width: 25%">/r/ URL (Legacy URL, backward compatibility)</th>
              <th style="width: 25%">Resolves To</th>
              <th style="width: 25%">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="url in section.items" :key="url.shorthand">
              <td>
                <template v-if="url.bare">
                  <code class="shorthand-code" @click="copyUrl(domain + url.bare)" role="button" :title="'Click to copy full URL'">
                    {{ url.bare }}
                  </code>
                  <span v-if="copied === domain + url.bare" class="text-success ms-2 small">Copied!</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <code class="shorthand-code" @click="copyUrl(domain + url.shorthand)" role="button" :title="'Click to copy full URL'">
                  {{ url.shorthand }}
                </code>
                <span v-if="copied === domain + url.shorthand" class="text-success ms-2 small">Copied!</span>
              </td>
              <td><code class="text-muted">{{ url.full }}</code></td>
              <td>{{ url.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="groupedSections.length === 0" class="text-center text-muted py-4">
      No URLs match your search.
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShorthandUrls',

  props: {
    urls: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      search: '',
      copied: null,
      domain: window.location.origin,
    };
  },

  computed: {
    filteredUrls() {
      if (!this.search) {
        return this.urls;
      }
      const q = this.search.toLowerCase();
      return this.urls.filter(
        (u) =>
          (u.bare && u.bare.toLowerCase().includes(q)) ||
          u.shorthand.toLowerCase().includes(q) ||
          u.full.toLowerCase().includes(q) ||
          u.description.toLowerCase().includes(q) ||
          u.section.toLowerCase().includes(q)
      );
    },

    groupedSections() {
      const groups = {};
      this.filteredUrls.forEach((url) => {
        if (!groups[url.section]) {
          groups[url.section] = [];
        }
        groups[url.section].push(url);
      });

      return Object.keys(groups).map((name) => ({
        name,
        items: groups[name],
      }));
    },
  },

  methods: {
    async copyUrl(fullUrl) {
      try {
        await navigator.clipboard.writeText(fullUrl);
        this.copied = fullUrl;
        setTimeout(() => {
          this.copied = null;
        }, 2000);
      } catch {
        // Silently fail if clipboard access is denied
      }
    },
  },
};
</script>

<style scoped>
.shorthand-code {
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.15s;
}

.shorthand-code:hover {
  background-color: #e9ecef;
}
</style>
